import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, HelpCircle, Shield, Clock, Users, CreditCard, Wrench, Sparkles, Building } from 'lucide-react';

const faqData = [
  {
    id: 1,
    icon: Shield,
    question: "Travaillez-vous avec des données sensibles ou internes ?",
    answer: `Oui, c'est **le cœur de notre expertise**.

Nous concevons des solutions basées sur des **données propriétaires**, souvent complexes (documents, bases internes, expertises non formalisées), avec :

• des **RAG sur données privées**,
• des réponses **sourcées et traçables**,
• des règles de gouvernance adaptées aux contraintes métier et réglementaires.`
  },
  {
    id: 2,
    icon: Building,
    question: "Confidentialité et hébergement des données",
    answer: `Nous travaillons avec de nombreux **acteurs du conseil, de la finance et de l'industrie**, pour lesquels la **sécurité des données est critique**.

Cela implique :

• un **hébergement sur nos serveurs en France**,
• des règles strictes de confidentialité,
• une séparation claire des environnements et des données.

👉 **Ces exigences sont appliquées à l'ensemble de nos clients**, quel que soit leur secteur.`
  },
  {
    id: 3,
    icon: HelpCircle,
    question: "Quels types de cas d'usage traitez-vous le plus souvent ?",
    answer: `Nous intervenons notamment sur :

• le **traitement documentaire à grande échelle**,
• les **assistants métiers et commerciaux**,
• la **recherche et la veille sectorielle**,
• l'**automatisation de workflows**,
• la **capitalisation des savoirs internes**,
• l'**aide à la décision commerciale** (comparatifs, argumentaires).

Si votre problématique implique **beaucoup de documents, de données ou d'expertise dispersée**, elle est probablement pertinente.`
  },
  {
    id: 4,
    icon: Clock,
    question: "Quelle est la durée moyenne d'un projet ?",
    answer: `La durée dépend du périmètre, mais elle se situe **généralement entre 1 semaine et 4 mois**.

À titre d'exemples :

• **1 semaine** : catégorisation massive de contenus ou mise en place d'un premier pipeline NLP.
• **2 semaines** : génération automatisée de fiches produits ou création d'une base concurrentielle.
• **1 à 2 mois** : assistants IA métiers, intégration dans ChatGPT ou Microsoft Teams.
• **3 à 4 mois** : RAG sectoriel à grande échelle ou refonte complète de workflows documentaires.

Les projets sont découpés en **jalons concrets**, avec des livrables exploitables dès les premières semaines.`
  },
  {
    id: 5,
    icon: CreditCard,
    question: "Quelles sont vos conditions de paiement ?",
    answer: `Une fois la **faisabilité validée** :

• **50 % au démarrage du projet**,
• **50 % à la livraison**.

Ce fonctionnement garantit un engagement réciproque et une exécution rapide.`
  },
  {
    id: 6,
    icon: Users,
    question: "Utilisez-vous des freelances ?",
    answer: `Non.

Nous disposons de **toutes les compétences en interne** (IA, data, back-end, front, déploiement).

Nous considérons que la **stabilité des équipes** est un facteur clé de succès pour des projets IA durables, notamment lorsqu'ils évoluent dans le temps.`
  },
  {
    id: 7,
    icon: Wrench,
    question: "Quel accompagnement après la livraison ?",
    answer: `La majorité de nos clients choisissent de poursuivre avec :

• un **contrat de maintenance**,
• du **suivi et des ajustements continus**,
• un accompagnement sur l'évolution des usages et des données.

Nous proposons des formules adaptées afin que la solution reste **performante, fiable et adoptée dans la durée**.`
  },
  {
    id: 8,
    icon: Sparkles,
    question: "Qu'est-ce qui nous différencie ?",
    answer: `Nous proposons à nos clients **ce que nous avons d'abord mis en place chez nous**.

Nos solutions IA sont issues d'un **retour d'expérience concret**, accumulé sur nos propres activités : traitement de bases documentaires, analyse sectorielle, RAG métier, automatisation de workflows.

Nous ne conseillons pas une approche théorique : nous déployons des technologies **éprouvées en production**, avec leurs limites et leurs bonnes pratiques.

Par ailleurs, notre **structure volontairement compacte, agile et polyvalente** nous permet :

• une **exécution rapide**, sans lourdeur organisationnelle,
• une **forte réactivité** dans les phases de conception et d'itération,
• un **impact direct sur les coûts**, avec des projets plus accessibles que ceux proposés par de grands cabinets.

👉 Moins de distance entre la stratégie et l'exécution, **plus de résultats concrets**.`
  },
  {
    id: 9,
    icon: Users,
    question: "Travaillez-vous avec nos équipes internes ?",
    answer: `Oui, systématiquement.

Nous cherchons à :

• comprendre vos usages réels,
• intégrer vos contraintes métier,
• former vos équipes,
• et vous rendre **autonomes sur les solutions déployées**.

L'adoption est aussi importante que la technologie.`
  }
];

const FAQItem = ({ item, isOpen, onToggle }) => {
  const Icon = item.icon;
  
  // Parse markdown-like text to JSX
  const parseText = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Handle bullet points
      if (line.startsWith('• ')) {
        const content = line.slice(2);
        return (
          <li key={idx} className="ml-4 text-white/70">
            {parseBold(content)}
          </li>
        );
      }
      // Handle emoji lines
      if (line.startsWith('👉')) {
        return (
          <p key={idx} className="mt-4 text-[#3dd3ee] font-medium">
            {parseBold(line)}
          </p>
        );
      }
      // Regular paragraph
      if (line.trim()) {
        return (
          <p key={idx} className="text-white/70 mb-2">
            {parseBold(line)}
          </p>
        );
      }
      return null;
    });
  };
  
  const parseBold = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, idx) => 
      idx % 2 === 1 ? <strong key={idx} className="text-white font-semibold">{part}</strong> : part
    );
  };

  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm hover:border-[#3dd3ee]/30 transition-all duration-300">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#3dd3ee]/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-[#3dd3ee]" />
          </div>
          <h3 className="text-lg font-semibold text-white pr-4">{item.question}</h3>
        </div>
        <div className="flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-[#3dd3ee]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/50" />
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-0">
          <div className="pl-16 border-l-2 border-[#3dd3ee]/30 ml-6">
            <div className="pl-4">
              {parseText(item.answer)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  const [openItems, setOpenItems] = useState([1]);
  const navigate = useNavigate();

  const toggleItem = (id) => {
    setOpenItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#3dd3ee]/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#c8aff0]/10 rounded-full blur-[150px]" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 text-sm font-medium text-white/70 mb-6">
            <HelpCircle className="w-4 h-4 text-[#fad24b]" />
            Questions fréquentes
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            FAQ —{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}>
              Vos questions
            </span>
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Retrouvez les réponses aux questions les plus courantes sur notre approche, nos méthodes et notre accompagnement.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="relative pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-4">
            {faqData.map((item) => (
              <FAQItem
                key={item.id}
                item={item}
                isOpen={openItems.includes(item.id)}
                onToggle={() => toggleItem(item.id)}
              />
            ))}
          </div>
          
          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-[#3dd3ee]/10 to-[#c8aff0]/10 rounded-3xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-4">
                Vous avez d'autres questions ?
              </h3>
              <p className="text-white/60 mb-6">
                Notre équipe est disponible pour répondre à toutes vos interrogations.
              </p>
              <button
                onClick={() => navigate('/#contact')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#3dd3ee] to-[#c8aff0] text-[#0a0a0b] font-semibold rounded-xl hover:scale-105 transition-transform duration-300"
              >
                Contactez-nous
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
